// lib/authToken.ts
// In-memory access token management + refresh scheduling and pub/sub.
// Access token deliberately NOT persisted (security + auto logout on tab close).

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

let accessToken: string | null = null;
let sessionId: number | null = null; // current session id (from login response)
let refreshTimeout: number | null = null; // retained but unused after removing proactive scheduling
let isRefreshing = false; // internal proactive refresh guard
let reactiveRefreshing = false; // interceptor-driven single flight flag
const reactiveQueue: { resolve: (v: string | null) => void; reject: (e: any) => void }[] = [];
const listeners = new Set<() => void>();
let lastTokenSetAt = 0; // ms timestamp when we last received an access token
// Debug header name provided by backend to diagnose refresh failures
const REFRESH_DEBUG_HEADER = 'x-refresh-debug';

// Auth bootstrap (silent refresh) --------------------------------------------
// Some parts of the app (e.g. cookie consent) run before the app-level boot
// effect completes. To avoid UI flicker (treating an authed user as a guest for
// a moment), we expose a single-flight bootstrap that attempts refresh once.
let bootstrapped = false;
let bootstrapPromise: Promise<boolean> | null = null;

export interface DecodedJwt {
  exp?: number; // seconds since epoch
  iat?: number;
  [k: string]: any;
}

export function getAccessToken() {
  return accessToken;
}

export function getSessionId() {
  return sessionId;
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((l) => {
    try { l(); } catch { /* noop */ }
  });
}

export function decodeJwt(token?: string | null): DecodedJwt | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload;
  } catch {
    return null;
  }
}

export function getTokenExpiry(): number | null {
  const dec = decodeJwt(accessToken);
  return dec?.exp ?? null;
}

export function setSessionId(id: number | null) {
  sessionId = id;
  notify();
}

export function setAccessToken(token: string | null) {
  accessToken = token;
  lastTokenSetAt = token ? Date.now() : 0;
  // Proactive scheduling disabled per spec (refresh only on 401 path)
  notify();
}

export function clearAuthState() {
  accessToken = null;
  sessionId = null;
  if (refreshTimeout) window.clearTimeout(refreshTimeout);
  refreshTimeout = null;
  notify();
}

// scheduleRefresh removed (refresh only reacts to 401 now)

export async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) return accessToken; // avoid parallel proactive refresh
  isRefreshing = true;
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      // Log server-provided debug notes to aid diagnostics (e.g. sid_hash_mismatch, no_match)
      try {
        const dbg = res.headers.get(REFRESH_DEBUG_HEADER);
        if (dbg) {
          // eslint-disable-next-line no-console
          console.warn('[auth:refreshAccessToken] status', res.status, 'notes:', dbg);
        }
      } catch { /* ignore */ }
      // Strict behavior per spec: immediately clear local auth state so app redirects to login
      clearAuthState();
      return null;
    }
    const data = await res.json().catch(() => ({}));
    if (data?.access_token) {
      setAccessToken(data.access_token);
      return data.access_token as string;
    }
    return null;
  } catch {
    clearAuthState();
    return null;
  } finally {
    isRefreshing = false;
  }
}

// Single-flight refresh for 401 handling (reactive). Returns fresh token or null.
export async function reactiveRefresh(): Promise<string | null> {
  if (reactiveRefreshing) {
    return new Promise((resolve, reject) => reactiveQueue.push({ resolve, reject }));
  }
  reactiveRefreshing = true;
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!res.ok) {
      // Log server-provided debug notes and enforce immediate logout
      try {
        const dbg = res.headers.get(REFRESH_DEBUG_HEADER);
        if (dbg) {
          // eslint-disable-next-line no-console
          console.warn('[auth:reactiveRefresh] status', res.status, 'notes:', dbg);
        }
      } catch { /* ignore */ }
      clearAuthState();
      const text = await res.text().catch(() => '');
      const err = new Error(text || 'refresh failed');
      reactiveQueue.splice(0).forEach(p => p.reject(err));
      return null;
    }
    const data = await res.json().catch(() => ({}));
    if (data?.access_token) {
      setAccessToken(data.access_token);
    }
    reactiveQueue.splice(0).forEach(p => p.resolve(accessToken));
    return accessToken;
  } catch (e) {
    const exp = getTokenExpiry();
    const now = Date.now() / 1000;
    if (!exp || exp <= now) {
      clearAuthState();
    }
    reactiveQueue.splice(0).forEach(p => p.reject(e));
    return null;
  } finally {
    reactiveRefreshing = false;
  }
}

// Utility to ensure we have a non-expired token, else try refresh once.
export async function ensureFreshToken(): Promise<string | null> {
  const exp = getTokenExpiry();
  if (!accessToken || !exp) return null;
  const now = Date.now() / 1000;
  // Avoid refreshing within first 5s after setting a brand new token to allow cookie propagation
  if (Date.now() - lastTokenSetAt < 5000) {
    return accessToken;
  }
  if (exp - now < 60) {
    return await refreshAccessToken();
  }
  return accessToken;
}

// BroadcastChannel (optional multi-tab sync) ----------------------------------
// Only sync logout events to quickly reflect session termination across tabs.
const bc = typeof window !== 'undefined' && 'BroadcastChannel' in window ? new BroadcastChannel('auth-sync') : null;
if (bc) {
  bc.onmessage = (ev) => {
    if (ev.data === 'logout') {
      clearAuthState();
    }
  };
}

export function broadcastLogout() {
  bc?.postMessage('logout');
}

// Device ID helper ------------------------------------------------------------
// Updated key per spec: trade_device_id (migrate from previous keys if present)
const DEVICE_KEY = 'trade_device_id';
const LEGACY_KEYS = ['ts_device_id', 'device_id_v1'];
export function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined') return 'unknown-device';
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    // migrate from legacy keys if they exist
    for (const k of LEGACY_KEYS) {
      const legacy = localStorage.getItem(k);
      if (legacy) {
        id = legacy;
        try { localStorage.setItem(DEVICE_KEY, id); } catch { /* ignore */ }
        break;
      }
    }
  }
  if (!id) {
    try {
      id = crypto.randomUUID();
    } catch {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

// Silent refresh (called on app boot / new tab). Returns boolean success
export async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!res.ok) {
      // Helpful debug on boot if refresh fails
      try {
        const dbg = res.headers.get(REFRESH_DEBUG_HEADER);
        if (dbg && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[auth:silentRefresh] status', res.status, 'notes:', dbg);
        }
      } catch { /* ignore */ }
      return false;
    }
    const data = await res.json().catch(() => ({}));
    if (data?.access_token) setAccessToken(data.access_token);
    return !!data?.access_token;
  } catch {
    return false;
  }
}

export function isAuthBootstrapped(): boolean {
  return bootstrapped;
}

// Run refresh once per page-load. Safe to call from multiple places.
export async function bootstrapAuth(): Promise<boolean> {
  if (bootstrapped) return !!accessToken;
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    try {
      return await silentRefresh();
    } finally {
      bootstrapped = true;
      bootstrapPromise = null;
      // Notify so subscribers can re-check auth-dependent state.
      notify();
    }
  })();
  return bootstrapPromise;
}

// Public IP fetch (cached) ----------------------------------------------------
let cachedIp: string | null = null;
let ipPromise: Promise<string | null> | null = null;
export async function getClientPublicIp(): Promise<string | null> {
  if (cachedIp) return cachedIp;
  if (ipPromise) return ipPromise;
  ipPromise = fetch('https://api.ipify.org?format=json')
    .then(r => r.json())
    .then(j => {
      cachedIp = typeof j?.ip === 'string' ? j.ip : null;
      return cachedIp;
    })
    .catch(() => null)
    .finally(() => { ipPromise = null; });
  return ipPromise;
}

// Multi-tab logout via localStorage event (fallback for BroadcastChannel)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'ts_logout') {
      clearAuthState();
    }
  });
}

export function emitLocalLogoutMarker() {
  try { localStorage.setItem('ts_logout', String(Date.now())); } catch { /* ignore */ }
}
